import { Logger } from 'typescript-log'
import { validateKeyTypes } from '../../../helpers/validation'
import { EpaperPublicationType } from './epaper-loader-defination'
import { getIssue } from './getIssue'

export async function getLatestIssuesFromMultiplePublications({
    logger,
    publications,
    caller,
}: {
    logger: Logger
    publications: EpaperPublicationType[]
    caller: string
}) {
    const data = await Promise.all(
        publications.map(async (publication) => {
            const issues = await getLatestIssuesFromPublication({
                logger: logger,
                caller: caller,
                numberOfIssues: 1,
                publication: publication,
            })
            return {
                ...issues.latestIssues[0],
                publicationName: publication,
                isPremium: PremiumPublicationsSet[publication],
            }
        }),
    )

    return data
}

export async function getLatestIssuesForTheWest({
    numberOfIssues,
    logger,
    caller,
}: {
    numberOfIssues: number
    logger: Logger
    caller: string
}): Promise<{ latestIssues: Issue[] }> {
    const dataTW = await getLatestIssuesFromPublication({
        logger: logger,
        caller: caller,
        numberOfIssues: numberOfIssues,
        publication: 'the-west-australian',
    })

    const dataST = await getLatestIssuesFromPublication({
        logger: logger,
        caller: caller,
        numberOfIssues: Math.floor(numberOfIssues / 7) + 1,
        publication: 'sunday-times',
    })
    return combinePublications(dataTW, dataST, numberOfIssues)
}

export function combinePublications(
    dataTW: {
        latestIssues: Issue[]
    },

    dataST: {
        latestIssues: Issue[]
    },
    numberOfIssues: number,
): { latestIssues: Issue[] } {
    let indexTW = 0
    let indexST = 0

    const latestIssues: Issue[] = []

    while (latestIssues.length != numberOfIssues) {
        if (indexTW < dataTW.latestIssues.length) {
            if (
                indexST < dataST.latestIssues.length &&
                dataST.latestIssues[indexST].date >
                    dataTW.latestIssues[indexTW].date
            ) {
                latestIssues.push(dataST.latestIssues[indexST])
                indexST++
            }
            //push TW, increment index
            else {
                latestIssues.push(dataTW.latestIssues[indexTW])
                indexTW++
            }
        }
    }
    return { latestIssues }
}

export async function getLatestIssuesFromPublication({
    logger,
    publication,
    numberOfIssues,
    caller,
}: {
    logger: Logger
    publication: EpaperPublicationType
    numberOfIssues: number
    caller: string
}) {
    try {
        const cuePublication =
            InternalPulicationToCuePublicationMap[publication]
        if (publication === 'default') {
            return await getLatestIssuesForTheWest({
                numberOfIssues,
                logger,
                caller,
            })
        }
        const data = await getIssue(
            logger,
            validateGetEPaper,
            numberOfIssues,
            cuePublication,
            caller,
        )

        const issues = data.issues.web

        const result: { latestIssues: Issue[] } = { latestIssues: [] }

        issues.forEach(function (item) {
            //we realistically shoulnd't have more than 1 section
            const { coverImageSmallUri, coverImageThumbnailUri, readerUrl } =
                item
            result.latestIssues.push({
                date: new Date(item.publishedDate),
                coverUrl: coverImageSmallUri,
                coverTumbnailUrl: coverImageThumbnailUri,
                url: readerUrl,
                publicationName: publication,
                isPremium: PremiumPublicationsSet[publication],
            })
        })

        return result
    } catch (error) {
        if (process.env.NODE_ENV === 'production') {
            logger.warn('Error while loading editions data')

            return {
                latestIssues: [],
            }
        }

        logger.debug('Error while loading editions data')

        return {
            latestIssues: [],
        }
    }
}

//this probably isn't enough validation
export function validateGetEPaper(value: any): EPaperResponseDTO {
    const errors = validateKeyTypes('curation', value, {
        organizationTitle: 'string',
        id: 'string',
        title: 'string',
    })

    if (errors) {
        throw new Error('EPaper response invalid\n    ' + errors.join('    \n'))
    }

    return value
}

export type Issue = {
    date: Date
    coverUrl: string
    coverTumbnailUrl: string
    url: string
    publicationName: EpaperPublicationType
    isPremium?: boolean
}

export interface EPaperResponseDTO {
    organizationTitle: string
    id: string
    title: string
    issues: { [key in 'web' | 'ios' | 'android']: IssueData[] }
}

interface IssueData {
    pageCount: number
    downloadPagesUrl: string
    coverImageSmallUri: string
    os: 'web' | 'android' | 'ios'
    coverImageUri: string
    published: 1
    updatedDate: Date
    title: string
    url: string //not the url to the reader
    sections: [
        {
            sectionName: string
            coverImageThumbnailUri: string
            coverImageSmallUri: string
            coverImageUri: string
            readerUrl: string
        },
    ]
    coverImageThumbnailUri: string
    publicUrl: string
    readerUrl: string
    publishedDate: Date
}

export const PremiumPublicationsSet: {
    [key in EpaperPublicationType]?: boolean
} = {
    'albany-advertiser': true,
    'augusta-margaret-river-times': false,
    'broome-advertiser': false,
    'bunbury-herald': false,
    'busselton-dunsborough-times': false,
    countryman: true,
    'geraldton-guardian': true,
    'great-southern-herald': true,
    'harvey-waroona-reporter': false,
    'kalgoorlie-miner': true,
    'the-kimberley-echo': true,
    'manjimup-bridgetown-times': true,
    'midwest-times': false,
    'narrogin-observer': true,
    'north-west-telegraph': false,
    'pilbara-news': false,
    'sound-telegraph': false,
    'south-western-times': true,
    'sunday-times': true,
    default: true,
    'the-west-australian': true,
    perthnow: false,
    joondalup: false,
    'western-suburbs': false,
    canning: false,
    'mandurah-coastal-times': false,
    wanneroo: false,
    stirling: false,
    central: false,
    southern: false,
    cockburn: false,
    fremantle: false,
    melville: false,
}

export const InternalPulicationToCuePublicationMap: {
    [key in EpaperPublicationType]: string
} = {
    'albany-advertiser': 'wan_albany',
    'broome-advertiser': 'wan_broome',
    'kalgoorlie-miner': 'wan_kal-miner',
    'pilbara-news': 'wan_pilbara-news',
    'sound-telegraph': 'wan_sound-telegraph',
    'south-western-times': 'wan_southwest-times',
    'the-kimberley-echo': 'wan_kimberley',
    countryman: 'wan_countryman',
    default: 'wan_west-australian', //will fetch TW and ST combined
    'augusta-margaret-river-times': 'wan_margaret-river',
    'bunbury-herald': 'wan_bunbury-herald',
    'busselton-dunsborough-times': 'wan_busselton',
    'geraldton-guardian': 'wan_geraldton',
    'great-southern-herald': 'wan_greatsh',
    'harvey-waroona-reporter': 'wan_harvey-waroona',
    'manjimup-bridgetown-times': 'wan_manjimup',
    'midwest-times': 'wan_midwest',
    'narrogin-observer': 'wan_narrogin',
    'north-west-telegraph': 'wan_northwesttele',
    perthnow: 'wan_joondalup',
    joondalup: 'wan_joondalup',
    'western-suburbs': 'wan_western-suburbs',
    canning: 'wan_canning',
    'mandurah-coastal-times': 'wan_coastal-times',
    wanneroo: 'wan_wanneroo',
    stirling: 'wan_stirling',
    central: 'wan_central',
    southern: 'wan_southern',
    cockburn: 'wan_cockburn',
    fremantle: 'wan_fremantle',
    melville: 'wan_melville',
    'sunday-times': 'wan_sunday-times',
    'the-west-australian': 'wan_west-australian',
}

export function createDisplayName(publication: string) {
    const title = publication
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    return title
}
